import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FaTruck, FaSignInAlt, FaEye, FaEyeSlash } from 'react-icons/fa'
import { useAuthStore } from '../store'
import { Button, Input } from '../components/common'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthStore()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  /*
   * 登录后回哪儿。两个来源：
   *   location.state.from —— 组件内主动跳登录时带的（React Router 状态）
   *   ?redirect= —— api.js 拦到 401 时带的（它在组件外，只能走地址栏）
   * 只接受站内相对路径，防止被构造成开放重定向。
   */
  const redirectParam = new URLSearchParams(location.search).get('redirect')
  const safeRedirect = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : null
  const from = location.state?.from?.pathname || safeRedirect || '/dashboard'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      await login(data.email, data.password) // 邮箱作为账号
      toast.success('登录成功！')
      navigate(from, { replace: true })
    } catch (error) {
      toast.error(error.message || '登录失败，请检查账号和密码')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
      {/* Header */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-primary-600 text-xl font-bold mb-6">
          <FaTruck className="text-2xl" />
          <span>物流集货平台</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">欢迎回来</h1>
        <p className="text-gray-500">登录您的账户，管理物流订单</p>
      </div>

      {/* New User Alert */}
      {location.state?.newUser && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <span className="text-green-600">✓</span>
          <span className="text-green-700">注册成功！请使用您的邮箱和密码登录。</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Input
          label="邮箱地址"
          type="email"
          placeholder="请输入注册邮箱"
          size="lg"
          error={errors.email?.message}
          {...register('email', {
            required: '请输入邮箱',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: '请输入有效的邮箱地址',
            },
          })}
        />

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="font-medium text-gray-700 text-sm">登录密码</label>
            <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-primary-600">
              忘记密码？
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="请输入密码"
              className={`w-full px-4 py-3 border rounded-md text-base focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-12 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              {...register('password', { required: '请输入密码' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="remember"
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            {...register('remember')}
          />
          <label htmlFor="remember" className="ml-2 text-sm text-gray-600">
            记住我
          </label>
        </div>

        <Button
          type="submit"
          size="lg"
          loading={isLoading}
          className="w-full"
          icon={<FaSignInAlt />}
        >
          登录
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">或</span>
          </div>
        </div>

        <p className="text-center text-gray-600">
          还没有账户？
          <Link to="/signup" className="text-primary-600 font-medium hover:text-primary-700 ml-1">
            立即注册
          </Link>
        </p>
      </form>
    </div>
  )
}
